import React from "react";

interface RuleContentRendererProps {
  content: string;
}

export function RuleContentRenderer({ content }: RuleContentRendererProps) {
  const paragraphs = content.split(/\n\s*\n/);

  return (
    <div className="space-y-3">
      {paragraphs.map((paragraph, pIdx) => {
        const lines = paragraph.split("\n").filter((l) => l.trim() !== "");

        // Check if all lines are bullet points
        const isBulletList = lines.every(
          (l) => l.trim().startsWith("- ") || l.trim().startsWith("• ")
        );

        if (isBulletList && lines.length > 0) {
          return (
            <ul key={pIdx} className="list-disc list-inside space-y-1">
              {lines.map((line, lIdx) => (
                <li key={lIdx} className="text-blue-800 text-sm">
                  {renderInlineFormatting(line.replace(/^[-•]\s*/, ""))}
                </li>
              ))}
            </ul>
          );
        }

        // Mixed content paragraph
        return (
          <div key={pIdx} className="space-y-1">
            {lines.map((line, lIdx) => {
              const trimmed = line.trim();

              if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
                return (
                  <ul key={lIdx} className="list-disc list-inside">
                    <li className="text-blue-800 text-sm">
                      {renderInlineFormatting(trimmed.replace(/^[-•]\s*/, ""))}
                    </li>
                  </ul>
                );
              }

              // Bold heading line: **text**
              const boldMatch = trimmed.match(/^\*\*(.+?)\*\*$/);
              if (boldMatch) {
                return (
                  <p key={lIdx} className="font-semibold text-blue-900 text-sm">
                    {boldMatch[1]}
                  </p>
                );
              }

              return (
                <p key={lIdx} className="text-blue-800 text-sm">
                  {renderInlineFormatting(trimmed)}
                </p>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function renderInlineFormatting(text: string): React.ReactNode {
  // Split by **bold** patterns
  const parts = text.split(/(\*\*.+?\*\*)/g);
  if (parts.length === 1) return text;

  return parts.map((part, i) => {
    const boldMatch = part.match(/^\*\*(.+?)\*\*$/);
    if (boldMatch) {
      return (
        <strong key={i} className="font-semibold">
          {boldMatch[1]}
        </strong>
      );
    }
    return part;
  });
}
